import { defineStore } from "pinia";
import { computed, ref } from "vue";

import {
  approveDraft,
  getApplicationHistory,
  getCandidateState,
  previewDraft,
  restoreArchivedApplication,
} from "@/api/candidatePages";
import { invalidateCompanyWishlistCache } from "@/cache/companyWishlistCache";
import { resourceCache } from "@/cache/resourceCache";
import { usePolling } from "@/composables/usePolling";
import { useUserStore } from "@/stores/user";
import type {
  ArchivedApplication,
  BrowserFillReport,
  BrowserSessionLog,
  CandidateDraft,
  DraftPreview,
  PagePhase,
  SubmissionRecord,
} from "@/types/candidatePages";
import { draftAutomationWorker } from "@/workers/backendJobs";

const HISTORY_CACHE_KEY = "application-history";
const HISTORY_TTL_MS = 30_000;
const HISTORY_STALE_TTL_MS = 5 * 60_000;

function isAbortError(caught: unknown): boolean {
  return caught instanceof DOMException && caught.name === "AbortError";
}

function errorText(caught: unknown, fallback: string): string {
  return caught instanceof Error ? caught.message : fallback;
}

export const useReviewStore = defineStore("review", () => {
  const phase = ref<PagePhase>("loading");
  const draft = ref<CandidateDraft | null>(null);
  const errorMessage = ref("");
  const action = ref<"preview" | "launch" | "approve" | null>(null);
  const preview = ref<DraftPreview | null>(null);
  const browserReport = ref<BrowserFillReport | null>(null);
  const sessionLogs = ref<BrowserSessionLog[]>([]);
  const submission = ref<SubmissionRecord | null>(null);
  const activeLogSessionId = ref("");
  let loadController: AbortController | null = null;
  let actionController: AbortController | null = null;
  let generation = 0;

  const isUpdating = computed(() => action.value !== null);
  const draftTitle = computed(
    () => `${draft.value?.selected_job.company ?? "Application"} application draft`,
  );

  const logPolling = usePolling({
    operation: (signal) => draftAutomationWorker.readLogs(activeLogSessionId.value, signal),
    onResult: (result) => {
      sessionLogs.value = result.logs;
    },
    onFailure: () => {
      // Browser reporting remains usable when a transient log refresh fails.
    },
    nextDelay: () => 1_000,
    retryDelay: () => 1_000,
  });

  function stopLogPolling(): void {
    activeLogSessionId.value = "";
    logPolling.stop();
  }

  function startLogPolling(sessionId: string): void {
    stopLogPolling();
    activeLogSessionId.value = sessionId;
    logPolling.start();
  }

  async function load(): Promise<void> {
    loadController?.abort();
    const controller = new AbortController();
    loadController = controller;
    const requestGeneration = ++generation;
    phase.value = draft.value ? "updating" : "loading";
    errorMessage.value = "";
    try {
      const nextDraft = (await getCandidateState(controller.signal)).latest_draft;
      if (loadController !== controller || generation !== requestGeneration || controller.signal.aborted) return;
      draft.value = nextDraft;
      phase.value = draft.value ? "success" : "empty";
    } catch (caught: unknown) {
      if (loadController !== controller || generation !== requestGeneration || isAbortError(caught)) return;
      phase.value = draft.value ? "success" : "error";
      errorMessage.value = errorText(caught, "The draft could not be loaded.");
    } finally {
      if (loadController === controller) loadController = null;
    }
  }

  function beginAction(nextAction: "preview" | "launch" | "approve"): AbortController | null {
    if (!draft.value || isUpdating.value) return null;
    actionController?.abort();
    const controller = new AbortController();
    actionController = controller;
    action.value = nextAction;
    errorMessage.value = "";
    return controller;
  }

  function ownsAction(controller: AbortController): boolean {
    return actionController === controller && !controller.signal.aborted;
  }

  function finishAction(controller: AbortController): void {
    if (!ownsAction(controller)) return;
    action.value = null;
    actionController = null;
  }

  async function showPreview(): Promise<void> {
    const controller = beginAction("preview");
    const activeDraft = draft.value;
    if (!controller || !activeDraft) return;
    try {
      const result = await previewDraft(activeDraft.id, controller.signal);
      if (ownsAction(controller)) preview.value = result;
    } catch (caught: unknown) {
      if (ownsAction(controller) && !isAbortError(caught)) {
        errorMessage.value = errorText(caught, "The preview could not be generated.");
      }
    } finally {
      finishAction(controller);
    }
  }

  async function launchAutomation(): Promise<void> {
    const controller = beginAction("launch");
    const activeDraft = draft.value;
    let logSessionId = "";
    if (!controller || !activeDraft) return;
    try {
      const launched = await draftAutomationWorker.launch(activeDraft.id, controller.signal);
      if (!ownsAction(controller)) return;
      if (!launched.ok || !launched.session_id) {
        throw new Error(launched.error || launched.message || "The browser session could not start.");
      }
      browserReport.value = launched;
      logSessionId = launched.session_id;
      startLogPolling(logSessionId);
      const completed = await draftAutomationWorker.run(
        activeDraft.id,
        launched.session_id,
        controller.signal,
      );
      if (!ownsAction(controller)) return;
      browserReport.value = completed;
      if (completed.logs?.length) sessionLogs.value = completed.logs;
    } catch (caught: unknown) {
      if (ownsAction(controller) && !isAbortError(caught)) {
        errorMessage.value = errorText(caught, "Automation paused before the review boundary.");
      }
    } finally {
      if (ownsAction(controller) && activeLogSessionId.value === logSessionId) {
        stopLogPolling();
      }
      finishAction(controller);
    }
  }

  async function recordApproval(): Promise<void> {
    const controller = beginAction("approve");
    const activeDraft = draft.value;
    if (!controller || !activeDraft) return;
    try {
      const result = await approveDraft(activeDraft.id, controller.signal);
      if (ownsAction(controller)) submission.value = result.submission;
    } catch (caught: unknown) {
      if (ownsAction(controller) && !isAbortError(caught)) {
        errorMessage.value = errorText(caught, "The review approval could not be recorded.");
      }
    } finally {
      finishAction(controller);
    }
  }

  function dispose(): void {
    generation += 1;
    loadController?.abort();
    actionController?.abort();
    loadController = null;
    actionController = null;
    action.value = null;
    stopLogPolling();
    if (phase.value === "updating") phase.value = draft.value ? "success" : "empty";
  }

  function reset(): void {
    dispose();
    phase.value = "loading";
    draft.value = null;
    errorMessage.value = "";
    preview.value = null;
    browserReport.value = null;
    sessionLogs.value = [];
    submission.value = null;
  }

  return {
    action,
    browserReport,
    dispose,
    draft,
    draftTitle,
    errorMessage,
    isUpdating,
    launchAutomation,
    load,
    preview,
    recordApproval,
    reset,
    sessionLogs,
    showPreview,
    submission,
    phase,
  };
});

export const useHistoryStore = defineStore("history", () => {
  const userStore = useUserStore();
  const phase = ref<PagePhase>("loading");
  const history = ref<ArchivedApplication[]>([]);
  const errorMessage = ref("");
  let activeController: AbortController | null = null;
  let restoreController: AbortController | null = null;
  let generation = 0;

  function applyHistory(nextHistory: ArchivedApplication[]): void {
    history.value = nextHistory;
    phase.value = history.value.length ? "success" : "empty";
  }

  async function load(): Promise<void> {
    activeController?.abort();
    const controller = new AbortController();
    activeController = controller;
    const requestGeneration = ++generation;
    phase.value = history.value.length ? "updating" : "loading";
    errorMessage.value = "";
    try {
      const scope = userStore.user?.id;
      const payload = scope
        ? await resourceCache.read({
            key: HISTORY_CACHE_KEY,
            loader: getApplicationHistory,
            onRevalidated: (fresh) => {
              if (generation === requestGeneration && !controller.signal.aborted) {
                applyHistory(fresh.history);
              }
            },
            scope,
            signal: controller.signal,
            staleTtlMs: HISTORY_STALE_TTL_MS,
            ttlMs: HISTORY_TTL_MS,
          })
        : await getApplicationHistory(controller.signal);
      if (activeController !== controller || generation !== requestGeneration || controller.signal.aborted) return;
      applyHistory(payload.history);
    } catch (caught: unknown) {
      if (activeController !== controller || generation !== requestGeneration || isAbortError(caught)) return;
      phase.value = history.value.length ? "success" : "error";
      errorMessage.value = errorText(caught, "Application history could not be loaded.");
    } finally {
      if (activeController === controller) activeController = null;
    }
  }

  async function restore(historyId: string): Promise<void> {
    restoreController?.abort();
    const controller = new AbortController();
    restoreController = controller;
    const scope = userStore.user?.id ?? null;
    errorMessage.value = "";
    try {
      await restoreArchivedApplication(historyId, controller.signal);
      if (
        restoreController !== controller ||
        controller.signal.aborted ||
        (userStore.user?.id ?? null) !== scope
      ) return;
      history.value = history.value.filter((record) => record.id !== historyId);
      phase.value = history.value.length ? "success" : "empty";
      if (scope) resourceCache.invalidate(scope, HISTORY_CACHE_KEY);
      invalidateCompanyWishlistCache(scope);
    } catch (caught: unknown) {
      if (restoreController !== controller || isAbortError(caught)) return;
      errorMessage.value = errorText(caught, "The application could not be restored.");
    } finally {
      if (restoreController === controller) restoreController = null;
    }
  }

  function dispose(): void {
    generation += 1;
    activeController?.abort();
    restoreController?.abort();
    activeController = null;
    restoreController = null;
  }

  function reset(): void {
    dispose();
    phase.value = "loading";
    history.value = [];
    errorMessage.value = "";
  }

  return { dispose, errorMessage, history, load, phase, reset, restore };
});
